import { useCallback, useEffect, useState } from 'react';

import ResizeObserver from 'lib/resize-observer';
import { ACTIVITY_BUILDER_A4_LONG_SIDE } from '../activity-builder.constants';

export const usePagePreviewResizeObserver = () => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const scaleActivityView = useCallback(() => {
    const container = containerRef?.childNodes[0] as HTMLElement;
    if (containerRef && container) {
      const ratio = containerRef.offsetWidth / ACTIVITY_BUILDER_A4_LONG_SIDE;
      container.style.transform = `scale(${ratio})`;
      container.style.transformOrigin = 'left top';
    }
  }, [containerRef]);

  useEffect(() => {
    if (containerRef) {
      const resizeObserver = new ResizeObserver(() => {
        scaleActivityView();
      });
      resizeObserver.observe(containerRef);
      return () => {
        resizeObserver.unobserve(containerRef);
      };
    }
  }, [containerRef, scaleActivityView]);

  return {
    containerRef,
    setContainerRef,
    scaleActivityView,
  };
};

export const usePagePreviewIntersectionObserver = ({
  initialVisibility,
  parentElement,
  containerElement,
}: {
  parentElement?: HTMLElement | null;
  containerElement?: HTMLDivElement | null;
  initialVisibility?: boolean;
}) => {
  const [isVisible, setIsVisible] = useState(initialVisibility || false);

  useEffect(() => {
    if (!containerElement || parentElement === undefined) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        root: parentElement,
        threshold: 0.5,
      }
    );

    observer.observe(containerElement);
    return () => {
      observer.unobserve(containerElement);
    };
  }, [containerElement, parentElement]);

  return {
    isVisible,
  };
};
